import { useMachine } from "@xstate/react";
import classnames from "classnames";
import { compareAsc, endOfDay } from "date-fns";
import { find, isEmpty, isNil, some } from "lodash";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { GainsightEvent, trackEvent } from "../../../core/common/gainsight";
import {
    documentDownloadMachine,
    DocumentDownloadMachineState,
} from "../../../core/workflows/documentDownload";
import {
    paymentAlreadyPaidMachine,
    PaymentAlreadyPaidMachineState,
} from "../../../core/workflows/paymentAlreadyPaid";
import {
    paymentFormMachine,
    PaymentFormMachineState,
} from "../../../core/workflows/paymentForm";
import {
    paymentRequestMachine,
    PaymentRequestMachineState,
} from "../../../core/workflows/paymentRequest";
import {
    PaymentMethodName,
    PaymentRequestStatus,
} from "../../../PaymentContainerClient/commons/enums";
import DocumentDto from "../../../PaymentContainerClient/dtos/DocumentDto";
import getFormattedMessageWithScope from "../../../utils/getFormattedMessageWithScope";
import getCreditorName from "../../commons/getCreditorName";
import getInstallmentById from "../../commons/getInstallmentById";
import { AlreadyPaidModal } from "../../components/AlreadyPaidModal";
import { FullPageSpinner } from "../../components/FullPageSpinner";
import { Header } from "../../components/Header";
import { useLanguage } from "../../components/LanguageProvider";
import { PaymentAlreadyExistModal } from "../../components/PaymentAlreadyExistModal";
import { PaymentFailedModal } from "../../components/PaymentFailedModal";
import { PaymentForm } from "../../components/PaymentForm";
import { PaymentSolutionsModal } from "../../components/PaymentSolutionsModal";
import { PayNowModal } from "../../components/PayNowModal";
import { PrivacyCookiePolicyFooter } from "../../components/PrivacyCookiePolicyFooter";
import { SpinnerIcon } from "../../components/SpinnerIcon";
import { Summary } from "../../components/Summary";
import { ToastVariant } from "../../components/Toast/Toast";
import { useToast } from "../../components/Toast/ToastProvider";
import { useWindowDimensions } from "../../components/WindowDimensionsProvider";
import "./style.css";

const fm = getFormattedMessageWithScope("views.Payment");
const fme = getFormattedMessageWithScope("errors");

export const Payment = () => {
    const { token } = useParams();
    const { addToast, removeAllToasts } = useToast();
    const { locale } = useLanguage();
    const { width, height } = useWindowDimensions();
    const [isDocumentsPreviewerOpen, setIsDocumentsPreviewerOpen] =
        useState(false);
    const [selectedDocumentForPreview, setSelectedDocumentForPreview] =
        useState<DocumentDto | undefined>(undefined);
    const [isPaymentAlreadyExistModalOpen, setIsPaymentAlreadyExistModalOpen] =
        useState(false);
    const [isPaymentSolutionsModalOpen, setIsPaymentSolutionsModalOpen] =
        useState(false);
    const [isPayNowModalOpen, setIsPayNowModalOpen] = useState(false);
    const [isOpeningPaymentSite, setIsOpeningPaymentSite] = useState(false);
    const [paymentRequestState, send] = useMachine(paymentRequestMachine);
    const [paymentFormState, sendPaymentForm] = useMachine(
        paymentFormMachine.withConfig({
            actions: {
                notifyErrorCreatingPayment: () =>
                    addToast(fme("errorCreatingPayment"), {
                        variant: ToastVariant.Error,
                        autoDismiss: false,
                    }),
            },
            services: {
                openPaymentLink: (context) => () => {
                    setIsOpeningPaymentSite(true);
                    setIsPaymentAlreadyExistModalOpen(false);
                    // Warning: need for browsers like Safari otherwise it
                    // doesn't open the Opening Payment Site spinner
                    setTimeout(() => {
                        window.open(context.paymentData?.link, "_self");
                    }, 50);
                },
            },
        })
    );
    const [paymentAlreadyPaidState, sendPaymentAlreadyPaid] = useMachine(
        paymentAlreadyPaidMachine.withConfig({
            actions: {
                notifyErrorSettingAlreadyPaid: () =>
                    addToast(fme("errorSettingAlreadyPaid"), {
                        variant: ToastVariant.Error,
                        autoDismiss: false,
                    }),
            },
            services: {
                reloadPage: () => () => window.location.reload(),
            },
        })
    );
    const [documentDownloadState, sendDocumentDownload] = useMachine(
        documentDownloadMachine.withConfig({
            actions: {
                notifyErrorDownloadingDocument: () =>
                    addToast(fme("errorDownloadingDocument"), {
                        variant: ToastVariant.Error,
                        autoDismiss: false,
                    }),
            },
        })
    );

    // Warning: this check is needed because of browsers like Safari. That means
    // if page is "cached" we need to hide the open payment site spinner (e.g.
    // when the user goes back with the browser back button).
    // It could be better to reload page? Think on it paying attention to all
    // kind of browsers.
    window.onpageshow = function (event: any) {
        if (event.persisted) {
            setIsOpeningPaymentSite(false);
        }
    };

    // Warning: this check is needed because of browsers like Safari. Unlike
    // Chrome, if the user goes back from the TsPay page to the PaymentContainer
    // page it doesn't reset the form state automatically.
    useEffect(() => {
        if (paymentFormState.value === PaymentFormMachineState.Success) {
            sendPaymentForm("RESET");
            removeAllToasts();
        }
    }, [paymentFormState.value, sendPaymentForm, removeAllToasts]);

    useEffect(() => {
        if (paymentFormState.context.currentStep === 0) {
            trackEvent(GainsightEvent.OpenPaymentRequestsTab);
        } else if (paymentFormState.context.currentStep === 1) {
            trackEvent(GainsightEvent.OpenPaymentPlansTab);
        } else if (paymentFormState.context.currentStep === 2) {
            trackEvent(GainsightEvent.OpenPaymentMethodsTab);
        }
    }, [paymentFormState.context.currentStep]);

    useEffect(() => {
        if (paymentRequestState.value === PaymentRequestMachineState.Idle) {
            send("FETCH", { token });
        } else if (
            !isNil(paymentRequestState.context.data) &&
            !paymentFormState.context.paymentPlanId &&
            isEmpty(paymentFormState.context.installmentIds)
        ) {
            sendPaymentForm("INITIALIZE", {
                paymentRequest: paymentRequestState.context.data,
            });
        }
    });

    useEffect(() => {
        if (height < 500) {
            if (isDocumentsPreviewerOpen) {
                toggleDocumentsPreviewer();
            }

            if (isPaymentSolutionsModalOpen) {
                togglePaymentSolutionsModal();
            }

            if (paymentRequestState.context.isPaymentFailedModalOpen) {
                closePaymentFailedModal();
            }
        }
    });

    function goToNextStep() {
        if (paymentFormState.context.currentStep === 1) {
            togglePaymentSolutionsModal();
        } else {
            sendPaymentForm("NEXT_STEP");
        }
    }

    function changeStep(step: number) {
        sendPaymentForm("SET_STEP", { step });
    }

    function setPayNow(isPayNow: boolean) {
        sendPaymentForm("SET_PAY_NOW", {
            isPayNow,
            paymentRequest: paymentRequestState.context.data,
        });
        togglePayNowModal();
    }

    function setPaymentPlanId(paymentPlanId: string) {
        sendPaymentForm("SET_PAYMENT_PLAN_ID", {
            paymentPlanId,
            paymentRequest: paymentRequestState.context.data,
        });
    }

    function addInstallmentId(installmentId: string) {
        sendPaymentForm("ADD_INSTALLMENT_ID", {
            installmentId,
            paymentRequest: paymentRequestState.context.data,
        });
    }

    function removeInstallmentId(installmentId: string) {
        sendPaymentForm("REMOVE_INSTALLMENT_ID", {
            installmentId,
            paymentRequest: paymentRequestState.context.data,
        });
    }

    function setPaymentMethod(paymentMethod?: PaymentMethodName) {
        sendPaymentForm("SET_PAYMENT_METHOD", { paymentMethod });
    }

    function submit() {
        trackEvent(GainsightEvent.ProceedToPayment, {
            scheduled: !paymentFormState.context.isPayNow,
            paymentPlan: find(
                paymentRequestState.context.data!.availablePaymentPlans,
                { id: paymentFormState.context.paymentPlanId }
            )!.type,
            paymentMethod: paymentFormState.context.paymentMethod,
        });
        sendPaymentForm("SUBMIT", {
            token,
            locale,
            paymentRequest: paymentRequestState.context.data,
        });
    }

    function toggleAlreadyPaidModal() {
        if (!paymentAlreadyPaidState.context.isModalOpen) {
            trackEvent(GainsightEvent.OpenPaymentRequestAlreadyPaidModal);
        }
        sendPaymentAlreadyPaid("TOGGLE_MODAL");
    }

    function closePaymentFailedModal() {
        send("CLOSE_PAYMENT_FAILED_MODAL");
    }

    function submitAlreadyPaid(note?: string) {
        trackEvent(GainsightEvent.ConfirmPaymentRequestIsAlreadyPaid);
        sendPaymentAlreadyPaid("SUBMIT", { token, note });
    }

    function toggleDocumentsPreviewer(document?: DocumentDto) {
        if (!isDocumentsPreviewerOpen) {
            trackEvent(GainsightEvent.OpenDocumentsPreview);
        }

        setSelectedDocumentForPreview(
            document && document.documentId ? document : undefined
        );
        setIsDocumentsPreviewerOpen(!isDocumentsPreviewerOpen);
    }

    function downloadDocument(document: DocumentDto) {
        trackEvent(GainsightEvent.DownloadDocument);
        sendDocumentDownload("DOWNLOAD", { token, document });
    }

    function togglePaymentAlreadyExistModal() {
        if (!isPaymentAlreadyExistModalOpen) {
            trackEvent(GainsightEvent.OpenPaymentAlreadyExistModal);
        }
        setIsPaymentAlreadyExistModalOpen(!isPaymentAlreadyExistModalOpen);
    }

    function togglePaymentSolutionsModal() {
        if (!isPaymentSolutionsModalOpen) {
            trackEvent(GainsightEvent.OpenPaymentSolutionsModal);
        }
        setIsPaymentSolutionsModalOpen(!isPaymentSolutionsModalOpen);
    }

    function togglePayNowModal() {
        setIsPayNowModalOpen(!isPayNowModalOpen);
    }

    function onConfirmPayOnDeadlineModal() {
        changeStep(2);
        togglePaymentSolutionsModal();
    }

    const atLeastOneSelectedInstallmentHasExpired = some(
        paymentFormState.context.installmentIds,
        (installmentId) =>
            compareAsc(
                new Date(),
                endOfDay(
                    new Date(
                        getInstallmentById(
                            installmentId,
                            paymentRequestState.context.data!
                        ).dueDate
                    )
                )
            ) === 1
    );

    return (
        <FullPageSpinner
            isLoading={isOpeningPaymentSite}
            message={fm("waitTrasnferToSecurePaymentSite")}
        >
            <div className="v-Payment-Container">
                <Header
                    creditorName={
                        paymentRequestState.context.data
                            ? getCreditorName(
                                  paymentRequestState.context.data?.creditor
                              )
                            : undefined
                    }
                    logoUrl={paymentRequestState.context.data?.creditor.logoUrl}
                />
                <div className="v-Payment-content-section">
                    <div className="v-Payment-left-section">
                        {paymentRequestState.value ===
                        PaymentRequestMachineState.Loading ? (
                            <div className="v-Payment-app-loading-icon-container">
                                <SpinnerIcon className="v-Payment-app-loading-icon" />
                            </div>
                        ) : paymentRequestState.value !==
                          PaymentRequestMachineState.Idle ? (
                            <>
                                <PaymentForm
                                    paymentRequest={
                                        paymentRequestState.context.data
                                    }
                                    error={paymentRequestState.context.error}
                                    currentStep={
                                        paymentFormState.context.currentStep
                                    }
                                    onChangeStep={changeStep}
                                    onSetPayNow={setPayNow}
                                    isPayNow={paymentFormState.context.isPayNow}
                                    onChangePaymentPlanId={setPaymentPlanId}
                                    selectedPaymentPlanId={
                                        paymentFormState.context.paymentPlanId
                                    }
                                    onAddInstallmentId={addInstallmentId}
                                    onRemoveInstallmentId={removeInstallmentId}
                                    selectedInstallmentIds={
                                        paymentFormState.context.installmentIds
                                    }
                                    onChangePaymentMethod={setPaymentMethod}
                                    selectedPaymentMethod={
                                        paymentFormState.context.paymentMethod
                                    }
                                    onOpenDocumentsPreviewer={
                                        toggleDocumentsPreviewer
                                    }
                                    selectedDocumentForPreview={
                                        selectedDocumentForPreview
                                    }
                                    isDownloadingDocument={
                                        documentDownloadState.value ===
                                        DocumentDownloadMachineState.Loading
                                    }
                                    isDocumentsPreviewerOpen={
                                        width > 1000 || isDocumentsPreviewerOpen
                                    }
                                    onCloseDocumentsPreviewer={
                                        toggleDocumentsPreviewer
                                    }
                                    onDownloadDocument={downloadDocument}
                                    documentIdDownloading={
                                        documentDownloadState.context.documentId
                                    }
                                />
                                <PrivacyCookiePolicyFooter />
                            </>
                        ) : undefined}
                    </div>
                    <div
                        className={classnames(
                            "v-Payment-right-section",
                            paymentRequestState.context.data?.status ===
                                PaymentRequestStatus.Paid
                                ? "v-Payment-right-section-request-paid"
                                : undefined
                        )}
                    >
                        {paymentRequestState.value ===
                            PaymentRequestMachineState.Loading &&
                        width > 1000 ? (
                            <div className="v-Payment-app-loading-icon-container">
                                <SpinnerIcon className="v-Payment-app-loading-icon" />
                            </div>
                        ) : paymentRequestState.value !==
                          PaymentRequestMachineState.Idle ? (
                            <Summary
                                paymentRequest={
                                    paymentRequestState.context.data
                                }
                                error={paymentRequestState.context.error}
                                onClickNext={goToNextStep}
                                currentStep={
                                    paymentFormState.context.currentStep
                                }
                                selectedInstallmentIds={
                                    paymentFormState.context.installmentIds
                                }
                                selectedPaymentMethodName={
                                    paymentFormState.context.paymentMethod
                                }
                                onSubmit={submit}
                                onOpenPaymentAlreadyExistModal={
                                    togglePaymentAlreadyExistModal
                                }
                                isCreatingPayment={
                                    paymentFormState.value ===
                                    PaymentFormMachineState.Submitting
                                }
                                onOpenAlreadyPaidModal={toggleAlreadyPaidModal}
                                isPayNow={paymentFormState.context.isPayNow}
                                selectedPaymentPlanId={
                                    paymentFormState.context.paymentPlanId
                                }
                                onDownloadDocument={downloadDocument}
                                isDownloadingDocument={
                                    documentDownloadState.value ===
                                    DocumentDownloadMachineState.Loading
                                }
                                documentIdDownloading={
                                    documentDownloadState.context.documentId
                                }
                            />
                        ) : undefined}
                    </div>
                </div>
                {paymentRequestState.context.data ? (
                    <PaymentFailedModal
                        visible={
                            paymentRequestState.context.isPaymentFailedModalOpen
                        }
                        onClose={closePaymentFailedModal}
                        paymentRequest={paymentRequestState.context.data}
                    />
                ) : undefined}
                <AlreadyPaidModal
                    visible={paymentAlreadyPaidState.context.isModalOpen}
                    onAbort={toggleAlreadyPaidModal}
                    onConfirm={submitAlreadyPaid}
                    loading={
                        paymentAlreadyPaidState.value ===
                        PaymentAlreadyPaidMachineState.Submitting
                    }
                />
                <PaymentSolutionsModal
                    visible={isPaymentSolutionsModalOpen}
                    onConfirm={onConfirmPayOnDeadlineModal}
                    onAbort={togglePaymentSolutionsModal}
                    hasSelectedExpiredInstallments={
                        atLeastOneSelectedInstallmentHasExpired
                    }
                    isPayNow={paymentFormState.context.isPayNow}
                    selectedPaymentPlanType={
                        find(
                            paymentRequestState.context.data
                                ?.availablePaymentPlans,
                            { id: paymentFormState.context.paymentPlanId }
                        )?.type
                    }
                />
                <PayNowModal
                    visible={isPayNowModalOpen}
                    onConfirm={togglePayNowModal}
                    isPayNow={paymentFormState.context.isPayNow}
                />
                <PaymentAlreadyExistModal
                    visible={isPaymentAlreadyExistModalOpen}
                    onAbort={togglePaymentAlreadyExistModal}
                    onConfirm={submit}
                    loading={
                        paymentFormState.value ===
                        PaymentFormMachineState.Submitting
                    }
                />
            </div>
        </FullPageSpinner>
    );
};
